import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Archive,
  Building2,
  Copy,
  Cpu,
  DollarSign,
  ExternalLink,
  FileIcon,
  FileSpreadsheet,
  FileText,
  Globe,
  Image as ImageIcon,
  Link2,
  Loader2,
  Mail,
  Paperclip,
  Plus,
  Presentation,
  RefreshCw,
  RotateCcw,
  Sparkles,
  Search,
  StickyNote,
  Tag,
  Trash2,
  TrendingUp,
  Users,
  X,
  ChevronDown,
  ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useDealsListStore } from "@/store/deals-list-store";
import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";
import { MOBILE_BREAKPOINT, useIsMobile } from "@/hooks/use-mobile";
import { inboxApi, InboxDetailResponse } from "@/api/inbox-api";
import {
  IInboundEmail,
  IInboundEmailAttachment,
  IInboundEmailExtractedInfo,
  InboxFilter,
  NEEDS_INFO_FILTER,
} from "@/types/data-models";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FilePreviewDialog } from "@/components/common/file-preview-dialog";
import { LinkToDealDialog } from "./link-to-deal-dialog";
import { nextExtractionState } from "./extraction-collapse";
import { DocSendPicker } from "@/components/common/docsend-picker";
import { AddDealDialog } from "@/components/deals/add-deal";
import {
  AutoCreateDealsDialog,
  candidatesFrom,
} from "./auto-create-deals-dialog";
import { useUser } from "@clerk/clerk-react";
import { cn, formatFileSize } from "@/lib/utils";
import { getErrorMessage } from "@/lib/api-error";
import { INBOX_EMAIL_DOMAIN } from "@/lib/constants/inbox";

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
}

// Attachment tiles read better with an icon that matches the file. Senders
// vary in how faithfully they set a content type, so fall back to the
// extension before giving up on the generic clip.
function attachmentIcon(att: IInboundEmailAttachment) {
  const type = att.content_type?.toLowerCase() ?? "";
  const name = att.file_name?.toLowerCase() ?? "";
  const hasExt = (...exts: string[]) =>
    exts.some((ext) => name.endsWith(`.${ext}`));

  if (type.startsWith("image/") || hasExt("png", "jpg", "jpeg", "gif", "webp", "heic", "svg"))
    return ImageIcon;
  if (type.includes("presentation") || type.includes("powerpoint") || hasExt("ppt", "pptx", "key"))
    return Presentation;
  if (type.includes("spreadsheet") || type.includes("excel") || type.includes("csv") || hasExt("xls", "xlsx", "csv", "numbers"))
    return FileSpreadsheet;
  if (type.includes("pdf") || type.includes("word") || type.startsWith("text/") || hasExt("pdf", "doc", "docx", "rtf", "txt", "md"))
    return FileText;
  return Paperclip;
}

// Extraction writes the literal "N/A" when it can't find a field, so treat
// that as "nothing extracted" rather than seeding it into a form.
function extractedValue(value?: string): string | undefined {
  return value && value !== "N/A" ? value : undefined;
}

/**
 * The extraction fields, in reading order. Data-driven rather than eight
 * near-identical blocks of JSX, because every one of them is now editable and
 * because a field the AI *missed* has to be addable — the old grid hid those
 * entirely, so there was no way to supply what it failed to find.
 *
 * Keys must stay in step with EDITABLE_EXTRACTED_FIELDS on the backend.
 */
type ExtractedFieldKey =
  | "company_name"
  | "founder_names"
  | "stage"
  | "funding_ask"
  | "traction"
  | "website"
  | "description"
  | "notes";

const EXTRACTED_FIELDS: {
  key: ExtractedFieldKey;
  label: string;
  icon: typeof Building2;
  span: string;
  multiline?: boolean;
  isLink?: boolean;
}[] = [
  { key: "company_name", label: "Company", icon: Building2, span: "" },
  { key: "founder_names", label: "Founders", icon: Users, span: "" },
  { key: "stage", label: "Stage", icon: Tag, span: "" },
  { key: "funding_ask", label: "Ask", icon: DollarSign, span: "" },
  { key: "traction", label: "Traction", icon: TrendingUp, span: "col-span-2" },
  { key: "website", label: "Website", icon: Globe, span: "col-span-2", isLink: true },
  {
    key: "description",
    label: "Description",
    icon: StickyNote,
    span: "col-span-2 lg:col-span-4",
    multiline: true,
  },
  {
    key: "notes",
    label: "Notes",
    icon: FileIcon,
    span: "col-span-2 lg:col-span-4",
    multiline: true,
  },
];

/**
 * One extraction field: reads as text, edits in place on click.
 *
 * An empty field still renders, as a muted "Add" affordance — correcting what
 * the AI got wrong and supplying what it never found are the same job.
 */
function EditableExtractedField({
  field,
  value,
  isOverridden,
  onSave,
}: {
  field: (typeof EXTRACTED_FIELDS)[number];
  value?: string;
  isOverridden: boolean;
  onSave: (next: string) => Promise<void>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const shown = extractedValue(value);
  const Icon = field.icon;

  const begin = () => {
    setDraft(shown ?? "");
    setIsEditing(true);
  };

  const commit = async () => {
    const next = draft.trim();
    setIsEditing(false);
    if (next === (shown ?? "")) return;
    setIsSaving(true);
    try {
      await onSave(next);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={cn(
        "bg-background p-4 transition-colors hover:bg-foreground/[0.01]",
        field.span,
      )}
    >
      <div className="mb-2 flex items-center gap-1.5 text-muted-foreground/70">
        <Icon className="h-3 w-3" />
        <span className="text-[10px] font-medium uppercase tracking-wider">
          {field.label}
        </span>
        {isOverridden && (
          <span
            className="text-[10px] font-medium normal-case text-muted-foreground/60"
            title="You edited this. The AI's original value is kept."
          >
            · edited
          </span>
        )}
        {isSaving && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
      </div>

      {isEditing ? (
        field.multiline ? (
          <textarea
            autoFocus
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            className="w-full resize-y rounded border border-border bg-background p-2 text-[13px] leading-relaxed text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        ) : (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") setIsEditing(false);
            }}
            className="w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        )
      ) : (
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={begin}
            aria-label={`Edit ${field.label}`}
            className="min-w-0 flex-1 text-left"
          >
            {shown ? (
              <span
                className={cn(
                  "text-sm font-medium text-foreground",
                  field.multiline && "block text-[13px] leading-relaxed",
                  !field.multiline && "block truncate",
                )}
              >
                {shown}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground/50">Add</span>
            )}
          </button>
          {shown && field.isLink && (
            <a
              href={shown.startsWith("http") ? shown : `https://${shown}`}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-muted-foreground/60 hover:text-foreground"
              aria-label={`Open ${field.label}`}
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

const FILTERS: { key: InboxFilter; label: string }[] = [
  { key: "needs_review", label: "Needs Review" },
  // Sits beside Needs Review rather than inside it: these are still in the
  // review pile, but they are the ones the automatic run could not read, and
  // the client asked to be able to work through exactly that set.
  { key: NEEDS_INFO_FILTER, label: "Needs Info" },
  { key: "linked", label: "Linked" },
  { key: "ignored", label: "Ignored" },
];

export default function InboxComponent() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { fetchDeals } = useDealsListStore();
  const { workspace } = useWorkspaceSettings();
  const navigate = useNavigate();
  const params = useParams<{ emailId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const inboundEmailEnabled = workspace?.inbound_email_enabled ?? false;
  const workspaceInboundEmail = workspace?.inbound_email_slug
    ? `${workspace.inbound_email_slug}@${INBOX_EMAIL_DOMAIN}`
    : null;

  const [activeFilter, setActiveFilter] =
    useState<InboxFilter>("needs_review");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce the search input so we aren't firing a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Selection — deep-links via either /inbox/:emailId (legacy) or
  // /inbox?selected=:emailId (new). Either is honored on first mount; the
  // internal state drives subsequent interactions.
  const initialSelected =
    params.emailId ?? searchParams.get("selected") ?? null;
  const [selectedId, setSelectedId] = useState<string | null>(initialSelected);
  const isMobile = useIsMobile();

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkingEmailId, setLinkingEmailId] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [autoCreateDialogOpen, setAutoCreateDialogOpen] = useState(false);
  const { user } = useUser();
  const [creatingFromEmailId, setCreatingFromEmailId] = useState<string | null>(
    null,
  );
  // Company name / website pulled off the email's extraction, seeded into the
  // create-deal form. Held as state (not derived inline) so its identity stays
  // stable while the dialog is open and can't clobber what the user types.
  const [createInitialValues, setCreateInitialValues] = useState<
    { companyName?: string; website?: string } | undefined
  >(undefined);

  // List query — per-filter cache so switching pills is instant for tabs
  // we've already loaded. `placeholderData: keepPreviousData` keeps the
  // previous filter's rows visible during the cross-fade so the UI never
  // flashes to empty while the new filter loads for the first time.
  const listQuery = useQuery({
    queryKey: [
      "inbox-list",
      activeWorkspaceId,
      activeFilter,
      debouncedSearch,
    ] as const,
    queryFn: () =>
      inboxApi.listEmails(
        activeWorkspaceId!,
        activeFilter,
        debouncedSearch || undefined,
      ),
    enabled: !!activeWorkspaceId,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const emails = listQuery.data?.emails ?? [];
  const counts = listQuery.data?.counts ?? {
    needs_review: 0,
    linked: 0,
    ignored: 0,
    needs_info: 0,
  };
  // Only show the skeleton on a true cold load — when we have cached data
  // from a prior filter it stays visible.
  const isListLoading = listQuery.isLoading;

  const invalidateInbox = () => {
    queryClient.invalidateQueries({ queryKey: ["inbox-list"] });
  };

  useEffect(() => {
    if (activeWorkspaceId) {
      fetchDeals(activeWorkspaceId);
    }
  }, [activeWorkspaceId, fetchDeals]);

  // If the URL carried a legacy /inbox/:emailId path, normalize to the new
  // query-param form so back/forward behaves predictably.
  useEffect(() => {
    if (params.emailId && activeWorkspaceId) {
      navigate(`/${activeWorkspaceId}/inbox?selected=${params.emailId}`, {
        replace: true,
      });
    }
  }, [params.emailId, activeWorkspaceId, navigate]);

  const handleSelect = (emailId: string) => {
    setSelectedId(emailId);
    const next = new URLSearchParams(searchParams);
    next.set("selected", emailId);
    setSearchParams(next, { replace: true });
  };

  const handleBack = () => {
    setSelectedId(null);
    const next = new URLSearchParams(searchParams);
    next.delete("selected");
    setSearchParams(next, { replace: true });
  };

  const handleLinkClick = (emailId: string) => {
    setLinkingEmailId(emailId);
    setLinkDialogOpen(true);
  };

  const handleLink = async (
    dealId: string,
    companyId?: string,
    fileTypes?: Record<string, string>,
  ) => {
    if (!linkingEmailId) return;
    setIsLinking(true);
    try {
      await inboxApi.linkToDeal(
        linkingEmailId,
        dealId,
        companyId,
        fileTypes,
        activeWorkspaceId ?? undefined
      );
      toast.success("Email linked to deal");
      setLinkDialogOpen(false);
      setLinkingEmailId(null);
      queryClient.invalidateQueries({ queryKey: ["inbox-email", linkingEmailId] });
      invalidateInbox();
    } catch {
      toast.error("Failed to link email");
    } finally {
      setIsLinking(false);
    }
  };

  const handleIgnore = async (emailId: string) => {
    try {
      await inboxApi.updateStatus(emailId, "ignored");
      toast.success("Email ignored");
      if (selectedId === emailId) setSelectedId(null);
      queryClient.invalidateQueries({ queryKey: ["inbox-email", emailId] });
      invalidateInbox();
    } catch {
      toast.error("Failed to update email");
    }
  };

  const handleRestore = async (emailId: string) => {
    try {
      await inboxApi.updateStatus(emailId, "needs_review");
      toast.success("Email restored");
      if (selectedId === emailId) setSelectedId(null);
      queryClient.invalidateQueries({ queryKey: ["inbox-email", emailId] });
      invalidateInbox();
    } catch {
      toast.error("Failed to restore email");
    }
  };

  const handleDelete = async (emailId: string) => {
    try {
      await inboxApi.deleteEmail(emailId);
      toast.success("Email deleted");
      if (selectedId === emailId) setSelectedId(null);
      queryClient.invalidateQueries({ queryKey: ["inbox-email", emailId] });
      invalidateInbox();
    } catch {
      toast.error("Failed to delete email");
    }
  };

  const handleCreateDealClick = (
    emailId: string,
    extracted?: IInboundEmailExtractedInfo,
  ) => {
    setCreatingFromEmailId(emailId);
    setCreateInitialValues({
      companyName: extractedValue(extracted?.company_name),
      website: extractedValue(extracted?.website),
    });
    setCreateDialogOpen(true);
  };

  // file_types are intentionally omitted: the attachments were already bound +
  // typed during deal creation, so passing them here would re-trigger ingestion
  // on top of the create-path sequence. This call only flips the email to
  // "linked" and attaches it to the now-created deal.
  const handleCreateDealSuccess = async (companyId: string) => {
    if (creatingFromEmailId && activeWorkspaceId) {
      try {
        await inboxApi.linkToDeal(
          creatingFromEmailId,
          companyId,
          companyId,
          undefined,
          activeWorkspaceId
        );
        toast.success("Deal created and email linked");
        invalidateInbox();
      } catch {
        toast.error("Deal created but failed to link email");
      }
    }
    setCreateDialogOpen(false);
    setCreatingFromEmailId(null);
    setCreateInitialValues(undefined);
    navigate(`/${activeWorkspaceId}/deals/${companyId}?tab=profile`);
  };

  const needsReviewCount = counts.needs_review;

  // Emails the server has already flagged as deal-worthy. Bulk creation reads
  // the same `auto_create_suggested` the per-email banner does, so the button
  // and the banner can never disagree.
  const autoCreateCandidates = candidatesFrom(emails);

  return (
    <div className="flex h-full overflow-hidden bg-background">
      {/* Left — message list (full-width on mobile, hidden once an email is open) */}
      <div
        className={cn(
          "flex h-full flex-col border-r border-border bg-background",
          isMobile
            ? selectedId
              ? "hidden"
              : "w-full"
            : "w-72 shrink-0",
        )}
      >
        {/* Header */}
        <div className="flex h-14 items-center justify-between border-b border-border px-4">
          <span className="text-sm font-semibold text-foreground">Inbox</span>
          <div className="flex items-center gap-2">
            {/* The whole point of forwarding a pile of emails: come back and
                make them all deals in one pass, rather than opening each one. */}
            {autoCreateCandidates.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 px-2 text-xs"
                onClick={() => setAutoCreateDialogOpen(true)}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Create {autoCreateCandidates.length}
              </Button>
            )}
            {needsReviewCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1.5 text-[10px] font-medium text-background">
                {needsReviewCount}
              </span>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="border-b border-border px-3 py-2">
          <div className="flex items-center gap-2 rounded-lg bg-foreground/[0.04] px-2.5 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/60 outline-none"
            />
          </div>
        </div>

        {/* Status filter pills */}
        <div className="flex gap-1 overflow-x-auto border-b border-border px-3 py-2 [scrollbar-width:none]">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={cn(
                "shrink-0 rounded px-2 py-1 text-xs transition-colors",
                activeFilter === f.key
                  ? "bg-foreground text-background font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]",
              )}
            >
              {f.label}
              {(counts[f.key] ?? 0) > 0 && activeFilter !== f.key && (
                <span className="ml-1 text-muted-foreground/60">
                  {counts[f.key]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Message list */}
        <div className="flex-1 overflow-y-auto">
          {isListLoading ? (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground">
              Loading...
            </div>
          ) : emails.length === 0 ? (
            <InboxListEmptyHint
              activeFilter={activeFilter}
              inboundEmailEnabled={inboundEmailEnabled}
              workspaceInboundEmail={workspaceInboundEmail}
              activeWorkspaceId={activeWorkspaceId}
            />
          ) : (
            emails.map((message) => (
              <InboxMessageRow
                key={message._id}
                message={message}
                activeFilter={activeFilter}
                isActive={message._id === selectedId}
                onSelect={() => handleSelect(message._id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Right — viewer (full-width on mobile only when an email is open) */}
      <div
        className={cn(
          "flex flex-col overflow-hidden bg-background",
          isMobile ? (selectedId ? "w-full" : "hidden") : "flex-1",
        )}
      >
        {selectedId ? (
          <>
            {isMobile && (
              <button
                onClick={handleBack}
                className="flex h-12 shrink-0 items-center gap-1.5 border-b border-border px-3 text-sm text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" />
                Inbox
              </button>
            )}
            <div className="min-h-0 flex-1 overflow-hidden">
              <InboxMessageViewer
                key={selectedId}
                emailId={selectedId}
                placeholder={emails.find((e) => e._id === selectedId) ?? null}
                onLinkClick={() => handleLinkClick(selectedId)}
                onCreateDealClick={(extracted) =>
                  handleCreateDealClick(selectedId, extracted)
                }
                onIgnore={() => handleIgnore(selectedId)}
                onRestore={() => handleRestore(selectedId)}
                onDelete={() => handleDelete(selectedId)}
                onRefresh={invalidateInbox}
              />
            </div>
          </>
        ) : (
          <InboxEmptyViewer />
        )}
      </div>

      <LinkToDealDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        onLink={handleLink}
        isLinking={isLinking}
        emailSubject={emails.find((e) => e._id === linkingEmailId)?.subject}
        emailBody={emails.find((e) => e._id === linkingEmailId)?.text}
        emailFrom={emails.find((e) => e._id === linkingEmailId)?.from_email}
        attachments={
          emails.find((e) => e._id === linkingEmailId)?.attachments
        }
        extractedInfo={
          emails.find((e) => e._id === linkingEmailId)?.extracted_info
        }
      />

      {activeWorkspaceId && user?.id && (
        <AutoCreateDealsDialog
          open={autoCreateDialogOpen}
          onOpenChange={setAutoCreateDialogOpen}
          emails={emails}
          workspaceId={activeWorkspaceId}
          userId={user.id}
          onCreated={invalidateInbox}
        />
      )}

      <AddDealDialog
        open={createDialogOpen}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) {
            setCreatingFromEmailId(null);
            setCreateInitialValues(undefined);
          }
        }}
        showTrigger={false}
        initialValues={createInitialValues}
        // The deal reached us through the workspace's inbound address, so the
        // source is already known — don't make the user pick it again.
        defaultSource="Email"
        emailId={creatingFromEmailId ?? undefined}
        emailAttachments={
          emails.find((e) => e._id === creatingFromEmailId)?.attachments
        }
        onDealCreated={handleCreateDealSuccess}
      />
    </div>
  );
}

// ── List row ─────────────────────────────────────────────────────────────────

function InboxMessageRow({
  message,
  activeFilter,
  isActive,
  onSelect,
}: {
  message: IInboundEmail;
  activeFilter: InboxFilter;
  isActive: boolean;
  onSelect: () => void;
}) {
  const linkedName = message.linked_company?.company_name;
  // The automatic run looked at this one and could not act on it. `failed` is
  // ours rather than the sender's, but from the inbox both mean the same
  // thing: it is still here and it needs a person.
  const needsInfo =
    message.auto_create_outcome === "insufficient_info" ||
    message.auto_create_outcome === "failed";

  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full border-b border-border px-4 py-3 text-left transition-colors hover:bg-foreground/[0.03]",
        isActive && "bg-foreground/[0.05]",
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-sm font-medium text-foreground">
          {message.from_name || message.from_email}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground/70">
          {formatTimeAgo(message.received_at)}
        </span>
      </div>
      <p className="mt-0.5 truncate text-xs text-muted-foreground">
        {message.subject || "(No Subject)"}
      </p>
      <div className="mt-1 flex items-center gap-2">
        {linkedName ? (
          <span className="flex items-center gap-1 rounded-full bg-foreground/[0.05] px-2 py-0.5 text-[10px] text-muted-foreground">
            <Link2 className="h-2.5 w-2.5" />
            {linkedName}
          </span>
        ) : needsInfo ? (
          // Takes the place of "Uncategorized": both describe an email nobody
          // has dealt with, but this one says why it is still sitting here.
          <span
            className="flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-700"
            title={
              message.auto_create_reason ??
              "We couldn't create a deal from this one automatically."
            }
          >
            <AlertCircle className="h-2.5 w-2.5" />
            Needs info
          </span>
        ) : activeFilter === "needs_review" ? (
          <span className="rounded-full border border-dashed border-border px-2 py-0.5 text-[10px] text-muted-foreground/70">
            Uncategorized
          </span>
        ) : null}
        {message.attachments.length > 0 && (
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground/70">
            <Paperclip className="h-2.5 w-2.5" />
            {message.attachments.length}
          </span>
        )}
      </div>
    </button>
  );
}

// ── List empty state ────────────────────────────────────────────────────────

function InboxListEmptyHint({
  activeFilter,
  inboundEmailEnabled,
  workspaceInboundEmail,
  activeWorkspaceId,
}: {
  activeFilter: InboxFilter;
  inboundEmailEnabled: boolean;
  workspaceInboundEmail: string | null;
  activeWorkspaceId: string | null;
}) {
  if (activeFilter !== "needs_review") {
    return (
      <div className="px-4 py-8 text-center text-xs text-muted-foreground">
        No messages.
      </div>
    );
  }

  const copyEmail = () => {
    if (!workspaceInboundEmail) return;
    navigator.clipboard.writeText(workspaceInboundEmail);
    toast.success("Email address copied");
  };

  return (
    <div className="px-4 py-6 text-center">
      {inboundEmailEnabled && workspaceInboundEmail ? (
        <>
          <p className="text-xs font-medium text-foreground">
            You&apos;re all caught up
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
            Forward deal emails to land them here.
          </p>
          <button
            type="button"
            onClick={copyEmail}
            className="mt-3 inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-muted px-2.5 py-1 font-mono text-[11px] text-foreground transition-colors hover:bg-background"
          >
            <span className="truncate">{workspaceInboundEmail}</span>
            <Copy className="h-3 w-3 shrink-0 text-muted-foreground" />
          </button>
        </>
      ) : (
        <>
          <p className="text-xs font-medium text-foreground">
            Set up inbound email
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
            Enable a workspace inbox and forward deal emails to it.
          </p>
          {activeWorkspaceId && (
            <Button asChild size="sm" variant="outline" className="mt-3 h-7 text-xs">
              <Link to={`/${activeWorkspaceId}/settings/inbox`}>
                Open settings
              </Link>
            </Button>
          )}
        </>
      )}
    </div>
  );
}

// ── Viewer ──────────────────────────────────────────────────────────────────

function InboxMessageViewer({
  emailId,
  placeholder,
  onLinkClick,
  onCreateDealClick,
  onIgnore,
  onRestore,
  onDelete,
  onRefresh,
}: {
  emailId: string;
  // The list row we already have in memory — lets us paint the header
  // (subject, sender, chips) instantly while the full body + thread
  // resolve from the network.
  placeholder: IInboundEmail | null;
  onLinkClick: () => void;
  onCreateDealClick: (extracted?: IInboundEmailExtractedInfo) => void;
  onIgnore: () => void;
  onRestore: () => void;
  onDelete: () => void;
  onRefresh: () => void;
}) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ["inbox-email", emailId] as const,
    queryFn: () => inboxApi.getEmail(emailId),
    staleTime: 60_000,
  });

  const email = detailQuery.data?.email ?? placeholder ?? null;
  const thread = useMemo(
    () => detailQuery.data?.thread ?? [],
    [detailQuery.data?.thread],
  );
  // Only show the full-pane loading state if we have nothing to paint —
  // when a placeholder is available the header + preview render instantly
  // and the body fades in once the detail response arrives.
  const showLoading = detailQuery.isLoading && !placeholder;

  const deckLinksByEmail = useMemo(
    () => detailQuery.data?.deck_links_by_email ?? {},
    [detailQuery.data?.deck_links_by_email],
  );
  const autoCreateSuggested = detailQuery.data?.auto_create_suggested ?? false;
  // Dismissal is persisted, but hide the banner immediately rather than
  // waiting for the refetch to come back and say so.
  const [autoCreateHidden, setAutoCreateHidden] = useState(false);

  // Fields the user has corrected, so the panel can mark them. Held locally as
  // well as server-side so the "edited" marker appears on save rather than
  // after the refetch lands.
  const [overriddenFields, setOverriddenFields] = useState<Set<string>>(
    new Set(),
  );

  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState("");
  // Body editing is per message, so the open editor is tracked by id.
  const [editingBodyId, setEditingBodyId] = useState<string | null>(null);
  const [bodyDraft, setBodyDraft] = useState("");
  // Toggled per message: show the as-received version instead of the edit.
  const [showingOriginal, setShowingOriginal] = useState<Set<string>>(new Set());

  const saveContent = async (
    id: string,
    content: { subject?: string; text?: string },
  ) => {
    try {
      await inboxApi.updateContent(id, content);
      queryClient.invalidateQueries({ queryKey: ["inbox-email", emailId] });
      queryClient.invalidateQueries({ queryKey: ["inbox-list"] });
    } catch (e) {
      toast.error(getErrorMessage(e, "Couldn't save that change"));
    }
  };

  const commitSubject = async () => {
    const next = subjectDraft.trim();
    setIsEditingSubject(false);
    if (!email || next === (email.subject ?? "")) return;
    await saveContent(email._id, { subject: next });
  };

  const commitBody = async (id: string, current?: string) => {
    const next = bodyDraft;
    setEditingBodyId(null);
    if (next === (current ?? "")) return;
    await saveContent(id, { text: next });
  };

  const saveExtractedField = async (key: string, next: string) => {
    try {
      // An empty value means "reset to whatever the AI said", which is what
      // null does server-side.
      await inboxApi.updateExtractedInfo(emailId, { [key]: next || null });
      setOverriddenFields((prev) => {
        const updated = new Set(prev);
        if (next) updated.add(key);
        else updated.delete(key);
        return updated;
      });
      queryClient.invalidateQueries({ queryKey: ["inbox-email", emailId] });
      queryClient.invalidateQueries({ queryKey: ["inbox-list"] });
    } catch (e) {
      toast.error(getErrorMessage(e, "Couldn't save that change"));
    }
  };

  // A deck link the user asked to import. Only meaningful once the email is
  // attached to a deal — DocSend import needs a company to file the file under.
  const [deckLinkToImport, setDeckLinkToImport] = useState<string | null>(null);
  const linkedCompanyId =
    typeof email?.linked_company === "object" && email?.linked_company
      ? (email.linked_company as { _id?: string })._id
      : undefined;

  const onImportDeckLink = (link: string) => {
    if (!linkedCompanyId) {
      // Rather than half-import into nothing, say what's missing and hand them
      // the action that fixes it.
      toast.info("Create or link a deal first, then import the deck.");
      onCreateDealClick(extractedInfo);
      return;
    }
    setDeckLinkToImport(link);
  };

  const [isRefreshing, setIsRefreshing] = useState(false);
  const isMobile = useIsMobile();
  // On a phone the panel starts closed and stays however the reader left it.
  // Collapsing it from the scroll handler resizes the scroll container in the
  // middle of a touch fling, and iOS stops momentum scrolling when that
  // happens — the body read as frozen partway down the message.
  // Read the width directly: useIsMobile reports false on its first render.
  const [isExtractionExpanded, setIsExtractionExpanded] = useState(
    () => window.innerWidth >= MOBILE_BREAKPOINT
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  // Measure the extracted-intelligence content so the panel can collapse via
  // an explicit height transition — smooth + reliable like the portal header,
  // whereas the grid-rows `fr` animation snapped. ResizeObserver re-measures
  // when the content changes (pending → completed).
  const extractionContentRef = useRef<HTMLDivElement>(null);
  const [extractionHeight, setExtractionHeight] = useState(0);

  useEffect(() => {
    const el = extractionContentRef.current;
    if (!el) return;
    const measure = () => setExtractionHeight(el.scrollHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // Re-attach once the body mounts: the no-placeholder load returns early
    // before the panel exists. (Switching emails remounts this pane via its
    // `key`, so no per-email dep is needed.)
  }, [showLoading]);

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const res = await detailQuery.refetch();
      if (res.data?.email.extracted_info?.status === "completed") {
        toast.success("Extraction complete");
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const extractedInfo = email?.extracted_info;
  const isExtractionComplete = extractedInfo?.status === "completed";
  const isExtractionPending = !extractedInfo || extractedInfo.status === "pending" || extractedInfo.status === "processing";


  const handleUnlink = async () => {
    if (isUnlinking) return;
    setIsUnlinking(true);
    // Optimistically flip the header to "needs_review" so the unlink feels
    // instant — without this the linked chip lingers until both the status
    // PUT and the refetch round-trip complete.
    queryClient.setQueryData<InboxDetailResponse>(
      ["inbox-email", emailId],
      (old) =>
        old
          ? { ...old, email: { ...old.email, status: "needs_review" } }
          : old,
    );
    try {
      await inboxApi.updateStatus(emailId, "needs_review");
      toast.success("Unlinked from deal");
      setShowUnlinkConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["inbox-email", emailId] });
      onRefresh();
    } catch {
      toast.error("Failed to unlink");
      // Restore the true server state on failure.
      queryClient.invalidateQueries({ queryKey: ["inbox-email", emailId] });
    } finally {
      setIsUnlinking(false);
    }
  };

  const allEmails = useMemo(() => {
    if (!email) return [];
    return [...thread, email].sort(
      (a, b) =>
        new Date(a.received_at).getTime() -
        new Date(b.received_at).getTime(),
    );
  }, [email, thread]);

  // Threading is back on, so a long reply chain now renders here in full.
  // Collapse everything but the message actually being read — the newest —
  // the way every mail client does, and let the user open the rest.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (showLoading) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (!email) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
        Email not found
      </div>
    );
  }

  // Deal routes use applicant_company as :id (per DealTransformed convention),
  // so navigate via linked_company._id rather than linked_deal._id (which is
  // the OpportunityApplication id).
  const linkedDealId = email.linked_company?._id;
  const linkedName = email.linked_company?.company_name;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-background px-8 py-6">
        {isEditingSubject ? (
          <input
            autoFocus
            value={subjectDraft}
            onChange={(e) => setSubjectDraft(e.target.value)}
            onBlur={commitSubject}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitSubject();
              if (e.key === "Escape") setIsEditingSubject(false);
            }}
            aria-label="Subject"
            className="mr-6 min-w-0 flex-1 rounded border border-border bg-background px-2 py-1 text-xl font-semibold tracking-tight text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setSubjectDraft(email.subject ?? "");
              setIsEditingSubject(true);
            }}
            aria-label="Edit subject"
            className="mr-6 min-w-0 flex-1 rounded text-left"
          >
            <h2 className="line-clamp-2 text-xl font-semibold tracking-tight text-foreground">
              {email.subject || "(No Subject)"}
            </h2>
          </button>
        )}

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-2">
          {email.status === "linked" && linkedDealId && linkedName ? (
            <div className="flex items-center gap-1">
              <Link
                to={`/${activeWorkspaceId}/deals/${linkedDealId}?tab=profile`}
                className="flex items-center gap-1.5 rounded-l-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.02] hover:text-foreground"
              >
                {linkedName}
                <ExternalLink className="h-3 w-3 opacity-60" />
              </Link>
              <button
                type="button"
                onClick={() => setShowUnlinkConfirm(true)}
                className="flex h-[30px] items-center justify-center rounded-r-lg border border-l-0 border-border px-2 text-muted-foreground/70 transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
                title="Unlink from deal"
                aria-label="Unlink from deal"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : email.status === "needs_review" ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onCreateDealClick(extractedInfo)}
                className="flex items-center gap-1.5 rounded-lg border border-border bg-foreground px-3 py-1.5 text-xs font-medium text-background shadow-sm transition-colors hover:bg-foreground/90"
              >
                <Plus className="h-3.5 w-3.5" />
                Create deal
              </button>
              <button
                type="button"
                onClick={onLinkClick}
                className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.02] hover:text-foreground"
              >
                <Link2 className="h-3.5 w-3.5" />
                Link
              </button>
            </div>
          ) : null}

          {email.status === "ignored" ? (
            <>
              <button
                type="button"
                onClick={onRestore}
                className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.02] hover:text-foreground"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Restore
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-600 transition-colors hover:bg-red-50 hover:border-red-300"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </>
          ) : (
            email.status !== "linked" && (
              <>
                <button
                  type="button"
                  onClick={onIgnore}
                  className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.02] hover:text-foreground"
                >
                  <Archive className="h-3.5 w-3.5" />
                  Ignore
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-600 transition-colors hover:bg-red-50 hover:border-red-300"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </>
            )
          )}
        </div>
      </div>

      <AlertDialog
        open={showUnlinkConfirm}
        onOpenChange={(open) => {
          if (!isUnlinking) setShowUnlinkConfirm(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink from deal?</AlertDialogTitle>
            <AlertDialogDescription>
              This email will move back to Needs Review
              {linkedName ? ` and be detached from ${linkedName}` : ""}. You can
              re-link it anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnlinking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // preventDefault stops Radix from auto-closing the dialog;
                // handleUnlink closes it itself on success.
                e.preventDefault();
                handleUnlink();
              }}
              disabled={isUnlinking}
            >
              {isUnlinking ? (
                <>
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  Unlinking...
                </>
              ) : (
                "Unlink"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete email?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this email from the inbox. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deal suggestion. Opt-in per workspace, and always a prompt — the
          server never creates anything on its own. */}
      {autoCreateSuggested && !autoCreateHidden && (
        <div className="flex flex-wrap items-center gap-3 border-b border-border bg-foreground/[0.03] px-6 py-3">
          <Sparkles className="h-4 w-4 shrink-0 text-foreground/70" />
          <p className="min-w-0 flex-1 text-[13px] text-foreground">
            Create a deal for{" "}
            <span className="font-semibold">
              {extractedValue(extractedInfo?.company_name) ?? "this company"}
            </span>{" "}
            from this email?
          </p>
          <button
            type="button"
            onClick={() => onCreateDealClick(extractedInfo)}
            className="rounded-lg bg-foreground px-3 py-1.5 text-xs font-medium text-background transition-colors hover:bg-foreground/90"
          >
            Review &amp; create
          </button>
          <button
            type="button"
            onClick={async () => {
              setAutoCreateHidden(true);
              try {
                await inboxApi.dismissAutoCreate(emailId);
              } catch {
                // Non-fatal: the banner is already gone for this session, and
                // the worst case is it returns after a reload.
              }
            }}
            className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.02] hover:text-foreground"
          >
            Not a deal
          </button>
        </div>
      )}

      {/* Extracted Info Section */}
      <div className="border-b border-border bg-background">
        <button
          type="button"
          onClick={() => setIsExtractionExpanded(!isExtractionExpanded)}
          className="flex w-full items-center justify-between border-b border-border/40 bg-foreground/[0.01] px-6 py-3 transition-colors hover:bg-foreground/[0.02]"
        >
          <div className="flex items-center gap-2">
            <div className="flex h-5 w-5 items-center justify-center rounded bg-foreground text-background shadow-sm">
              <Cpu className="h-3 w-3" />
            </div>
            <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground">
              Extracted Intelligence
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {isExtractionPending && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRefresh();
                }}
                disabled={isRefreshing}
                className="h-7 px-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
              >
                <RefreshCw className={cn("mr-1.5 h-3 w-3", isRefreshing && "animate-spin")} />
                Check Status
              </Button>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                isExtractionExpanded ? "rotate-180" : "rotate-0"
              )}
            />
          </div>
        </button>

        <div
          className="overflow-hidden transition-[height,opacity] duration-300 ease-out"
          style={{
            height: isExtractionExpanded ? extractionHeight || "auto" : 0,
            opacity: isExtractionExpanded ? 1 : 0,
          }}
        >
          <div ref={extractionContentRef}>
            {isExtractionPending ? (
              <div className="px-6 py-10">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-foreground/[0.04] shadow-inner">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Analyzing document context...
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Neural extraction models are structuring the company data.
                    </p>
                  </div>
                </div>
              </div>
            ) : extractedInfo?.status === "failed" ? (
              <div className="px-6 py-6">
                <p className="text-sm font-medium text-red-500">
                  Extraction failed. Please manually link this email.
                </p>
              </div>
            ) : isExtractionComplete && extractedInfo ? (
              <div className="grid grid-cols-2 gap-[1px] bg-border/40 lg:grid-cols-4">
                {EXTRACTED_FIELDS.map((field) => (
                  <EditableExtractedField
                    key={field.key}
                    field={field}
                    value={extractedInfo[field.key] as string | undefined}
                    isOverridden={overriddenFields.has(field.key)}
                    onSave={(next) => saveExtractedField(field.key, next)}
                  />
                ))}
                {extractedInfo.low_confidence && (
                  <div className="col-span-2 bg-amber-500/[0.02] p-4 lg:col-span-4">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                      <span className="text-xs font-medium text-amber-600/90">
                        Low confidence extraction — verify manually. Click any
                        field to correct it.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Body / thread. Scrolling into a long email auto-collapses the
          extracted-intelligence panel above for more reading room, and
          re-expands it near the top; the 80/8 hysteresis avoids thrashing at
          the threshold. */}
      <div
        className="flex-1 space-y-12 overflow-y-auto bg-background px-8 py-8"
        onScroll={(e) => {
          if (isMobile) return;
          const el = e.currentTarget;
          const next = nextExtractionState({
            scrollTop: el.scrollTop,
            clientHeight: el.clientHeight,
            scrollHeight: el.scrollHeight,
            extractionHeight,
          });
          if (next !== "keep") setIsExtractionExpanded(next === "expand");
        }}
      >
        {allEmails.map((item, idx) => {
          // The last message is the one being read; earlier ones in the thread
          // start collapsed to a single header line.
          const isLast = idx === allEmails.length - 1;
          const isOpen = isLast || expandedIds.has(item._id);

          if (!isOpen) {
            return (
              <button
                key={item._id}
                type="button"
                onClick={() => toggleExpanded(item._id)}
                aria-expanded={false}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left transition-colors hover:bg-foreground/[0.03]",
                  idx > 0 && "border-t border-border/50",
                )}
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border/50 bg-foreground/[0.03]">
                  <span className="text-[11px] font-medium text-foreground">
                    {(item.from_name || item.from_email || "?")
                      .charAt(0)
                      .toUpperCase()}
                  </span>
                </div>
                <span className="shrink-0 text-[13px] font-medium text-foreground">
                  {item.from_name || item.from_email}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground">
                  {item.text?.replace(/\s+/g, " ").trim() || "(No content)"}
                </span>
                {item.attachments.length > 0 && (
                  <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/70">
                    {item.attachments.length}&nbsp;att
                  </span>
                )}
                <span className="shrink-0 text-[11px] uppercase tracking-wider text-muted-foreground/60">
                  {new Date(item.received_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </button>
            );
          }

          return (
          <div
            key={item._id}
            className={cn(
              "relative",
              idx > 0 && "border-t border-border/50 pt-10",
            )}
          >
            <div className="mb-8 flex items-start justify-between gap-6">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border/50 bg-foreground/[0.03]">
                  <span className="text-[14px] font-medium text-foreground">
                    {(item.from_name || item.from_email || "?")
                      .charAt(0)
                      .toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 pt-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-semibold text-foreground">
                      {item.from_name || item.from_email}
                    </span>
                    <span className="truncate text-[13px] text-muted-foreground">
                      &lt;{item.from_email}&gt;
                    </span>
                  </div>
                  <div className="mt-1 flex flex-col gap-0.5 text-[12px] text-muted-foreground/80">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-muted-foreground">
                        To:
                      </span>
                      <span className="truncate">{item.to}</span>
                    </div>
                    {item.cc && (
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-muted-foreground">
                          Cc:
                        </span>
                        <span className="truncate">{item.cc}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 flex-col items-end gap-2 pt-1">
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">
                  {new Date(item.received_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}{" "}
                  ·{" "}
                  {new Date(item.received_at).toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </div>

            {(() => {
              const isEdited = !!item.content_edited_at;
              const viewingOriginal = showingOriginal.has(item._id);
              // Only the original retains HTML — an edit writes plain text, so
              // there is no user-authored markup going through the renderer.
              const html = viewingOriginal ? item.original_html : item.html;
              const text = viewingOriginal ? item.original_text : item.text;

              if (editingBodyId === item._id) {
                return (
                  <div className="space-y-2">
                    <textarea
                      autoFocus
                      rows={14}
                      value={bodyDraft}
                      onChange={(e) => setBodyDraft(e.target.value)}
                      aria-label="Message body"
                      className="w-full resize-y rounded border border-border bg-background p-3 font-sans text-sm leading-relaxed text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => commitBody(item._id, item.text)}
                        className="rounded-lg bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:bg-foreground/90"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingBodyId(null)}
                        className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </button>
                      <span className="text-[11px] text-muted-foreground/70">
                        Saving switches this message to plain text. The original
                        is kept.
                      </span>
                    </div>
                  </div>
                );
              }

              return (
                <div className="group/body relative">
                  <div className="mb-2 flex items-center gap-2 text-[11px] text-muted-foreground/70">
                    {isEdited && (
                      <>
                        <span>Edited</span>
                        <button
                          type="button"
                          onClick={() =>
                            setShowingOriginal((prev) => {
                              const next = new Set(prev);
                              if (next.has(item._id)) next.delete(item._id);
                              else next.add(item._id);
                              return next;
                            })
                          }
                          className="underline underline-offset-2 hover:text-foreground"
                        >
                          {viewingOriginal ? "Show edited" : "View original"}
                        </button>
                      </>
                    )}
                    {!viewingOriginal && (
                      <button
                        type="button"
                        onClick={() => {
                          setBodyDraft(item.text ?? "");
                          setEditingBodyId(item._id);
                        }}
                        className="ml-auto opacity-0 transition-opacity focus:opacity-100 group-hover/body:opacity-100 underline underline-offset-2 hover:text-foreground"
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  {html ? (
                    <div
                      className="prose prose-sm max-w-none leading-relaxed text-foreground/90"
                      dangerouslySetInnerHTML={{
                        __html: html.replace(
                          /<img[^>]*\ssrc=["']cid:[^"']*["'][^>]*\/?>/gi,
                          "",
                        ),
                      }}
                    />
                  ) : (
                    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground/90">
                      {text || "(No content)"}
                    </pre>
                  )}
                </div>
              );
            })()}

            {/* Founders often send the deck as a link rather than a file. We
                already found it; make it actionable instead of leaving the
                user to copy it out of the body by hand. */}
            {(deckLinksByEmail[item._id] ?? []).length > 0 && (
              <div className="mt-8 border-t border-border/30 pt-4">
                <h4 className="mb-2 text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60">
                  Deck link
                </h4>
                <div className="space-y-2">
                  {(deckLinksByEmail[item._id] ?? []).map((link) => (
                    <div
                      key={link}
                      className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5"
                    >
                      <Presentation className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="min-w-0 flex-1 truncate text-xs text-foreground hover:underline"
                        title={link}
                      >
                        {link}
                      </a>
                      <button
                        type="button"
                        onClick={() => onImportDeckLink(link)}
                        className="shrink-0 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-foreground/[0.04]"
                      >
                        Import
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {item.attachments.length > 0 && (
              <div className="mt-8 border-t border-border/30 pt-4">
                <h4 className="mb-2 text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60">
                  {item.attachments.length}{" "}
                  {item.attachments.length === 1 ? "attachment" : "attachments"}
                </h4>
                {/* Two-up only once the reading pane is genuinely wide —
                    at sm the pane is still narrow enough to chop names. */}
                <div className="grid gap-2 lg:grid-cols-2">
                  {item.attachments.map((att, attIdx) => {
                    const key = att.file_upload_id ?? `att-${attIdx}`;
                    const name = att.file_name || "Untitled attachment";
                    const Icon = attachmentIcon(att);
                    const subtitle = !att.file_url
                      ? "Unavailable"
                      : att.file_size
                        ? formatFileSize(att.file_size)
                        : null;
                    // Name and size share a min-w-0 column so a long file name
                    // ellipsizes instead of squeezing the icon out of shape.
                    const tile = (
                      <>
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-border bg-muted/40">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-xs font-medium text-foreground">
                            {name}
                          </span>
                          {subtitle && (
                            <span className="text-[10px] tabular-nums text-muted-foreground/70">
                              {subtitle}
                            </span>
                          )}
                        </div>
                      </>
                    );

                    if (!att.file_url) {
                      return (
                        <div
                          key={key}
                          title={name}
                          className="flex items-center gap-3 rounded-lg border border-dashed border-border px-3 py-2.5 opacity-60"
                        >
                          {tile}
                        </div>
                      );
                    }

                    return (
                      <FilePreviewDialog
                        key={key}
                        url={att.file_url}
                        fileName={att.file_name}
                        fileId={att.file_upload_id}
                        contentType={att.content_type}
                      >
                        <button
                          type="button"
                          title={name}
                          aria-label={`View ${name}`}
                          className="group flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 text-left transition-colors hover:bg-foreground/[0.02]"
                        >
                          {tile}
                          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground/60 transition-colors group-hover:text-foreground" />
                        </button>
                      </FilePreviewDialog>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>

      {deckLinkToImport && linkedCompanyId && (
        <DocSendPicker
          open
          onClose={() => setDeckLinkToImport(null)}
          companyId={linkedCompanyId}
          opportunityId={activeWorkspaceId || undefined}
          mode="import"
          defaultUrl={deckLinkToImport}
          onImportComplete={() => {
            setDeckLinkToImport(null);
            toast.success("Deck imported to the deal");
          }}
        />
      )}
    </div>
  );
}

function InboxEmptyViewer() {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-foreground/[0.04]">
        <Mail className="h-5 w-5 text-muted-foreground/60" />
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        Select a message to read
      </p>
    </div>
  );
}
